import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useQuery } from '@tanstack/react-query';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import { toast } from '@hivebrite/design-system/Toast';

import { Notice } from 'hb-react/shared/components/Notice';
import { usePaymentContext } from 'hb-react/shared/components/Payment/context/PaymentContext';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

export const Paypal = () => {
  const {
    paymentAccountConfig: {
      external_account_id: clientId,
      type: gateway,
      legacy_to_charge_object: paymentObject,
    },
    paymentEndpoint,
    paymentData,
    amount,
    currency,
    onSuccess,
  } = usePaymentContext();

  const {
    data: { client_token: clientToken } = {},
    isLoading,
    error,
  } = useQuery({
    queryKey: ['paypal-token'],
    queryFn: () =>
      axios
        .post(Routes.paypal_client_token_path(), paymentObject)
        .then(({ data }) => data),
  });

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    errorReporting.captureException(error);

    return (
      <Notice notice={s__('PayPal|An error occurred processing the payment')} />
    );
  }

  const createOrder = () =>
    axios
      .post(Routes.paypal_create_order_path(), {
        ...paymentObject,
        amount,
        currency,
      })
      .then(({ data }) => data.id);

  const onApprove = async ({
    orderID,
    payerID,
  }: {
    orderID: string;
    payerID?: string | null;
  }) => {
    try {
      const { data } = await axios.post(paymentEndpoint, {
        ...paymentData,
        billing: {
          gateway,
          transaction_token: orderID,
          payer_id: payerID,
        },
      });

      // @ts-expect-error: fix types; onSuccess is a function
      onSuccess(data);
    } catch (e) {
      toast.error(s__('PayPal|An error occurred processing the payment'));
      errorReporting.captureException(e);
    }
  };

  return (
    <Box
      width="300px"
      display="block"
      data-testid="payment-paypal"
      id="paypal-button-container"
    >
      <PayPalScriptProvider
        options={{
          clientId,
          dataClientToken: clientToken,
          components: 'buttons',
          commit: true,
          currency,
        }}
      >
        <PayPalButtons
          style={{ layout: 'horizontal' }}
          onApprove={onApprove}
          createOrder={createOrder}
        />
      </PayPalScriptProvider>
    </Box>
  );
};
